import axios from 'axios';

import { BFF_BASE_URL } from '../auth/authApi';
import { Checks } from '../quality/qualityApi';

export type PromptSource = 'recorded' | 'current';

/** How a replay compares with the reply it re-answered, as judged by the admin. */
export type Verdict = 'better' | 'same' | 'worse';

export type ReplayStep =
    | {
          type: 'tool';
          name: string;
          args: Record<string, unknown>;
          result: string | null;
          /** Not executed: a replay never runs a tool that could change something. */
          stubbed: boolean;
          /** Called by the graph itself (record check, forced search), not the model. */
          forced: boolean;
      }
    | { type: 'reply'; content: string }
    | { type: 'guard'; content: string };

export interface ReplayRun {
    id: string;
    status: 'running' | 'done' | 'failed';
    model: string;
    provider: string | null;
    prompt_source: PromptSource;
    prompt_versions: Record<string, { id: string | null; version: number | null }> | null;
    original_reply: string | null;
    original_model: string | null;
    reply: string | null;
    steps: ReplayStep[];
    duration_ms: number | null;
    error: string | null;
    created_at: string;
    finished_at: string | null;
    verdict: Verdict | null;
    verdict_note: string | null;
    /** The automatic checks on the replay's reply; null on runs from before they existed. */
    checks: Checks | null;
}

export interface StartReplay {
    messageId: string;
    model?: string;
    promptSource: PromptSource;
    repeat: number;
}

/** Models worth comparing against the one in production; any OpenRouter id also works. */
export const REPLAY_MODEL_SUGGESTIONS = [
    'qwen/qwen3-30b-a3b-instruct-2507',
    'qwen/qwen3-235b-a22b-2507',
    'deepseek/deepseek-v3.2',
    'openai/gpt-4.1-mini',
    'google/gemini-2.5-flash-lite',
];

export const startReplay = async ({ messageId, model, promptSource, repeat }: StartReplay) => {
    const response = await axios.post<{ run_ids: string[] }>(`${BFF_BASE_URL}/admin/replays`, {
        message_id: messageId,
        model: model?.trim() || undefined,
        prompt_source: promptSource,
        repeat,
    });
    return response.data.run_ids;
};

export const listReplays = async (messageId: string): Promise<ReplayRun[]> => {
    const response = await axios.get<{ items: ReplayRun[] }>(
        `${BFF_BASE_URL}/admin/messages/${encodeURIComponent(messageId)}/replays`
    );
    return response.data.items;
};

export const errorMessage = (err: unknown, fallback: string): string =>
    axios.isAxiosError(err) ? err.response?.data?.error || err.message : fallback;

export type Feedback = 'up' | 'down';

export interface BatchSummary {
    total: number;
    done: number;
    failed: number;
    running: number;
    reply_changed: number;
    /** Runs where the agent tried to call a tool that changes something (blocked in a replay). */
    tried_to_write: number;
    /** Runs where the graph had to tell the model it claimed something no tool did. */
    guard_fired: number;
    verdicts: Record<Verdict | 'unjudged', number>;
    /** Runs whose reply failed at least one automatic check. */
    checks_failed: number;
    avg_duration_ms: number | null;
}

export interface BatchCriteria {
    feedback: Feedback | null;
    user_id: string | null;
    limit: number | null;
    message_ids: number | null;
}

export interface BatchListItem {
    id: string;
    model: string;
    prompt_source: PromptSource;
    criteria: BatchCriteria | null;
    total: number;
    skipped: number;
    done: number;
    failed: number;
    running: number;
    created_at: string;
}

export interface BatchRun {
    id: string;
    user_message_id: string;
    user_text: string | null;
    status: ReplayRun['status'];
    original_reply: string | null;
    original_tools: string[];
    reply: string | null;
    summary: { tools: string[]; blocked: string[]; guard_fired: boolean };
    duration_ms: number | null;
    error: string | null;
    verdict: Verdict | null;
    verdict_note: string | null;
    checks: Checks | null;
}

export interface BatchDetail {
    id: string;
    model: string;
    prompt_source: PromptSource;
    criteria: BatchCriteria | null;
    skipped: number;
    created_at: string;
    summary: BatchSummary;
    runs: BatchRun[];
}

export interface StartReplayBatch {
    feedback: Feedback;
    /** Only this user's replies; every user's when empty. */
    userId?: string;
    limit: number;
    model?: string;
    promptSource: PromptSource;
}

export const startReplayBatch = async ({ feedback, userId, limit, model, promptSource }: StartReplayBatch) => {
    const response = await axios.post<{ batch_id: string; run_ids: string[]; skipped: number }>(
        `${BFF_BASE_URL}/admin/replay-batches`,
        {
            feedback,
            user_id: userId || undefined,
            limit,
            model: model?.trim() || undefined,
            prompt_source: promptSource,
        }
    );
    return response.data;
};

export const listReplayBatches = async (): Promise<BatchListItem[]> => {
    const response = await axios.get<{ items: BatchListItem[] }>(`${BFF_BASE_URL}/admin/replay-batches`);
    return response.data.items;
};

export const getReplayBatch = async (batchId: string): Promise<BatchDetail> => {
    const response = await axios.get<BatchDetail>(
        `${BFF_BASE_URL}/admin/replay-batches/${encodeURIComponent(batchId)}`
    );
    return response.data;
};

/** Judges a finished run against the original reply; a null verdict clears it. */
export const setReplayVerdict = async (runId: string, verdict: Verdict | null, note?: string) => {
    const response = await axios.put<{ id: string; verdict: Verdict | null; verdict_note: string | null }>(
        `${BFF_BASE_URL}/admin/replays/${encodeURIComponent(runId)}/verdict`,
        { verdict, note: note?.trim() || undefined }
    );
    return response.data;
};
